/**
 * Mini-Project Detail Panel — the split-screen right-pane analog of
 * ProjectDetailPanel for Mini-Projects.
 *
 * Task #213. PortfolioPage mounts this in the right pane when the user
 * selects an MP row in the KpiGridTable. It mirrors the structure of
 * the full MiniProjectDetailPage (Progress / About / Media tabs) but
 * drops the PillarPageShell hero — the map already owns that visual
 * real estate at the top of the portfolio. The header is a slim band:
 *
 *   [Title]  [lifecycle chip]                [Settings ⚙ | Close ×]
 *   ───────────────────────────────────────────────────────────────
 *   [Progress | About | Media]
 *   ───────────────────────────────────────────────────────────────
 *   <active tab content>
 *
 * Click-on-KPI-dot wiring (per Skip's 2026-05-27 decision, Option A):
 * the portfolio grid passes `autoOpenKpi` + `autoOpenKpiRequestId` so
 * the panel mounts on Progress and auto-opens the matching KPI modal
 * (Schedule / Mission / Budget / Data Reliability). Summary click
 * routes here with autoOpenKpi=null — Progress opens with no modal,
 * which is the "see everything" affordance.
 */

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiMiniProject } from "../../api/useApiMiniProject";
import { isUserSystemAdmin } from "../../security/securityAdapter";
import ProgressTab from "./ProgressTab";
import MiniProjectSettingsModal from "./MiniProjectSettingsModal";
import MiniProjectCompletionDialog from "../../Components/MiniProjectCompletionDialog/MiniProjectCompletionDialog";
import SectionEditorModal from "../../Components/SectionEditorModal/SectionEditorModal";
import PlaybookCreationStepper from "../../Components/PlaybookCreationStepper/PlaybookCreationStepper";
import { localizedProjectStatus } from "../../Data/ProjectConfig/projectStandardOptions";
import { miniLifecycleToProjectStatus } from "../../Data/MiniProjectConfig/miniProjectLifecycle";

export type MiniProjectAutoOpenKpi =
  | "summary"   // Summary dot → StatusWriter/StatusReader flow (Skip 2026-05-27).
                // PortfolioPage already passes this at runtime; the type lagged,
                // and that single mismatch broke `tsc --noEmit && vite build`,
                // freezing the deployed bundle at a pre-auth-gate build (DEV-1297).
  | "schedule"
  | "mission"
  | "budget"
  | "dataReliability"
  | null;

interface Props {
  miniProjectGuid: string;
  currentUserGuid: string;
  /** Close the right pane (returns to PortfolioSummaryPanel). */
  onClose: () => void;
  /** Task #213. Auto-open a specific KPI modal when the panel mounts.
   *  Set by PortfolioPage when the user clicked a KPI dot. Bump
   *  `autoOpenKpiRequestId` to re-trigger if the user closes and
   *  re-clicks the same dot. */
  autoOpenKpi?: MiniProjectAutoOpenKpi;
  autoOpenKpiRequestId?: number;
}

export default function MiniProjectDetailPanel({
  miniProjectGuid,
  currentUserGuid,
  onClose,
  autoOpenKpi,
  autoOpenKpiRequestId,
}: Props) {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const location = useLocation();
  const { miniProject, loading, error, notFound, refetch } = useApiMiniProject(miniProjectGuid);

  // Lead permission gate — same as MiniProjectDetailPage. Strict Lead
  // match by OwnerUserGuid, OR sysadmin (CLAUDE.md isUserSystemAdmin
  // fast-path). Without the sysadmin OR a support user can't reach the
  // Settings modal on someone else's MP.
  const strictlyLead = miniProject?.ownerUserGuid?.toUpperCase() === currentUserGuid?.toUpperCase();
  const isSysadmin = isUserSystemAdmin(currentUserGuid as any);
  const isLead = !!(strictlyLead || isSysadmin);

  // Per Skip's 2026-05-27 simplification: split-screen panel shows only
  // the Progress tab — no tab bar, no About/Media. Users who want the
  // full page (with hero, About, Media, Settings hero crop, etc.) click
  // "View Full" to navigate to /mini-projects/{guid}. Mirrors the FP
  // compact view's "View Full → /projects/{guid}" affordance.
  const canReadProgress = miniProject?.canReadProgress ?? false;

  const [settingsOpen, setSettingsOpen] = useState(false);
  // U1b (DEV-1265): completion dialog + retrospective editor, also wired
  // here so the split-screen panel gets the full Mark-Complete flow.
  const [completionOpen, setCompletionOpen] = useState(false);
  const [retroOpen, setRetroOpen] = useState(false);
  const [playbookOpen, setPlaybookOpen] = useState(false);
  const [stripDismissed, setStripDismissed] = useState(false);

  // Re-fetch the MP after Settings save so the header reflects edits
  // (title change, lifecycle transition, visibility flip) without a
  // page reload. DEV-1275: onSaved must NOT close the modal — the modal
  // closes itself via onClose() on a metadata Save/Cancel, while lifecycle
  // transitions (Activate / Complete / Reopen) call onSaved alone and
  // intentionally keep the modal open. Closing here was firing on every
  // transition. Mirrors the Page's onSaved={refetch}.
  const handleSettingsSaved = () => {
    refetch();
  };

  // "View Full" — navigate to the canonical MP detail page on the
  // Management tab. Mirrors ProjectDetailPanel.handleViewFull's pattern
  // for FPs (DEV-649 / DEV-655 context-aware return state):
  //   - Route to /projects/<guid>/management (was /mini-projects/<guid>,
  //     which had no matching <Route> — fell through to a 404 that
  //     mis-rendered as "Community not found").
  //   - Land on Management explicitly (Skip 2026-05-27 — the split
  //     screen only ever shows the Management surface, so landing on
  //     Management when opening full view preserves the user's frame
  //     of reference).
  //   - Pass returnTo / returnLabel / restoreProjectGuid in router
  //     state so the destination page can render a "Return to split
  //     view" button that snaps back to the portfolio with this MP
  //     still selected. The page reads location.state to decide
  //     whether to render onRestore.
  // Task #233 will retire the `/mini-projects/` namespace platform-wide.
  const handleViewFull = () => {
    navigate(`/projects/${miniProjectGuid}/management`, {
      state: {
        returnTo: location.pathname,
        returnLabel: "Portfolio",
        restoreProjectGuid: miniProjectGuid,
      },
    });
  };

  if (loading) {
    return (
      <div style={panelStyle}>
        <div style={statusStyle}>Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={panelStyle}>
        <div style={statusStyle}>Failed to load: {error}</div>
      </div>
    );
  }
  if (notFound || !miniProject) {
    return (
      <div style={panelStyle}>
        <div style={statusStyle}>Mini-Project not found.</div>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      {/* ── Slim header band ──────────────────────────────────────
          Title + lifecycle chip on the left; View Full / Settings /
          Close actions on the right. No tab bar — the panel only
          shows Progress (per Skip's 2026-05-27 simplification). The
          KPI dot row lives inside ProgressTab and renders directly
          below this header, which is the layout Skip asked for. */}
      <div style={headerRowStyle}>
        <div style={titleColStyle}>
          <div style={titleStyle}>{miniProject.title}</div>
          {miniProject.status ? (
            <span style={lifecycleChipStyle} title={`Lifecycle: ${miniProject.status}`}>
              {/* DEV-1271: friendly lifecycle label, not the raw state. */}
              {localizedProjectStatus(
                miniLifecycleToProjectStatus(miniProject.status) || miniProject.status,
              )}
            </span>
          ) : null}
        </div>
        <div style={actionsColStyle}>
          <button
            type="button"
            style={viewFullBtnStyle}
            onClick={handleViewFull}
            title="View full Mini-Project page"
          >
            View Full
          </button>
          {isLead ? (
            <button
              type="button"
              style={iconBtnStyle}
              onClick={() => setSettingsOpen(true)}
              title="Settings"
              aria-label="Settings"
            >
              {/* Inline gear icon — keeps the panel free of additional asset deps */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          ) : null}
          <button
            type="button"
            style={iconBtnStyle}
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* U1b (decision #15): post-completion re-entry strip — Lead-only,
          completed non-master projects. Reachable after the completion
          dialog is dismissed. */}
      {isLead && !miniProject.isMaster && miniProject.status === "complete" && !stripDismissed ? (
        <div style={completionStripStyle}>
          <span aria-hidden="true">🏁</span>
          <button type="button" style={completionStripBtnStyle} onClick={() => setRetroOpen(true)}>
            📝 {t("miniProject.completion.retroTitle")}
          </button>
          <button type="button" style={{ ...completionStripBtnStyle, color: "var(--color-success)" }} onClick={() => setPlaybookOpen(true)}>
            🌱 {t("miniProject.completion.playbookTitle")}
          </button>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => setStripDismissed(true)}
            aria-label={t("miniProject.completion.close")}
            style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      ) : null}

      {/* ── Progress content (scrollable) ─────────────────────────
          ProgressTab is the only view in the split-screen panel.
          Users who need About / Media / hero / Settings hero crop
          click "View Full" → /mini-projects/{guid}. */}
      <div style={tabContentStyle}>
        {canReadProgress ? (
          <ProgressTab
            miniProject={miniProject}
            isLead={isLead}
            currentUserGuid={currentUserGuid}
            onMutated={refetch}
            autoOpenKpi={autoOpenKpi}
            autoOpenKpiRequestId={autoOpenKpiRequestId}
          />
        ) : (
          <div style={statusStyle}>
            You don't have access to this Mini-Project's Progress view.
            Click <em>View Full</em> to see the About and Media tabs.
          </div>
        )}
      </div>

      {isLead ? (
        <MiniProjectSettingsModal
          isOpen={settingsOpen}
          miniProject={miniProject}
          isLead={isLead}
          currentUserGuid={currentUserGuid}
          onClose={() => setSettingsOpen(false)}
          onSaved={handleSettingsSaved}
          onRequestComplete={() => {
            setSettingsOpen(false);
            setCompletionOpen(true);
          }}
        />
      ) : null}

      {/* U1b completion dialog. The split-screen panel has no About tab,
          so "Write retrospective" navigates to the full page's About tab. */}
      {isLead && completionOpen ? (
        <MiniProjectCompletionDialog
          miniProject={miniProject}
          canCreatePlaybook={isLead}
          onClose={() => setCompletionOpen(false)}
          onCompleted={refetch}
          onWriteRetrospective={() => {
            setCompletionOpen(false);
            setRetroOpen(true);
          }}
          onCreatePlaybook={() => {
            setCompletionOpen(false);
            setPlaybookOpen(true);
          }}
        />
      ) : null}

      {/* U2 playbook creation stepper — on success navigate into the master. */}
      {isLead && playbookOpen ? (
        <PlaybookCreationStepper
          source={miniProject}
          onClose={() => setPlaybookOpen(false)}
          onCreated={(playbook) => {
            setPlaybookOpen(false);
            if (playbook.masterMiniProjectGuid) {
              navigate(`/projects/${playbook.masterMiniProjectGuid}/about`);
            }
          }}
        />
      ) : null}

      {/* U1b retrospective editor — write in place (panel has no About tab,
          so this is the only frictionless path here). */}
      {isLead && retroOpen ? (
        <SectionEditorModal
          entityType="mini_project"
          entityGuid={miniProjectGuid}
          findOrCreateType="retrospective"
          onClose={() => setRetroOpen(false)}
          onSaved={refetch}
        />
      ) : null}
    </div>
  );
}

// ── Inline styles ────────────────────────────────────────────────
// Local-scope styles to avoid pulling in a module CSS file for a
// component that's intentionally slim. All colors use design tokens
// per CLAUDE.md theming rules.

const panelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  background: "var(--bg-surface)",
  color: "var(--text-primary)",
  overflow: "hidden",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 14px",
  borderBlockEnd: "1px solid var(--border-default)",
  background: "var(--bg-surface)",
  flexShrink: 0,
};

const titleColStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
  flex: 1,
};

// U1b post-completion strip (panel variant).
const completionStripStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 14px",
  background: "color-mix(in srgb, var(--color-success) 9%, transparent)",
  borderBlockEnd: "1px solid var(--border-subtle)",
  fontSize: 12.5,
  flexShrink: 0,
};

const completionStripBtnStyle: React.CSSProperties = {
  background: "var(--bg-hover)",
  border: "1px solid var(--border-default)",
  borderRadius: 7,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--info-text)",
  cursor: "pointer",
};

const titleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: "var(--text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const lifecycleChipStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  padding: "2px 8px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.06)",
  color: "var(--text-secondary)",
  border: "1px solid var(--border-default)",
  flexShrink: 0,
};

const actionsColStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexShrink: 0,
};

const iconBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--text-secondary)",
  padding: 6,
  borderRadius: 4,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const viewFullBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border-default)",
  color: "var(--text-primary)",
  cursor: "pointer",
  padding: "5px 10px",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
};

const tabContentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: 12,
};

const statusStyle: React.CSSProperties = {
  padding: 24,
  textAlign: "center",
  color: "var(--text-secondary)",
};
